/**
 * Validates and processes numeric input for heatmap position values
 * Returns null if input is invalid, otherwise returns the processed value
 */
export function validateNumericInput(
  value: string,
  min: number = -100,
  max: number = 100,
  maxDecimalPlaces: number = 6
): { isValid: boolean; numericValue?: number } {
  // Allow partial inputs like ".", "-", "-." while typing
  // Full pattern: optional minus, optional digits, optional dot, optional digits
  if (value !== '' && !/^-?(\d*\.?\d*)?$/.test(value)) {
    return { isValid: false };
  }

  // Limit to max decimal places
  const decimalIndex = value.indexOf('.');
  if (
    decimalIndex !== -1 &&
    value.length - decimalIndex - 1 > maxDecimalPlaces
  ) {
    return { isValid: false };
  }

  // Don't allow multiple leading zeros (e.g., "001", "00.5")
  if (/^-?0{2,}/.test(value)) {
    return { isValid: false };
  }

  // Allow empty string or partial inputs like ".", "-", "-."
  if (value === '' || value === '.' || value === '-' || value === '-.') {
    return { isValid: true, numericValue: 0 };
  }

  const parsedValue = parseFloat(value);

  // Check if it's a valid number and within range
  if (isNaN(parsedValue) || parsedValue > max || parsedValue < min) {
    return { isValid: false };
  }

  return { isValid: true, numericValue: parsedValue };
}

/**
 * Converts color stop inputs (string positions) to color stops (numeric positions)
 */
export function convertColorStopInputsToColorStops(
  colorStopInputs: [string, string][]
): [number, string][] {
  return colorStopInputs.map(([pos, color]) => [parseFloat(pos) || 0, color]);
}

/**
 * Validates color stop positions to ensure they maintain correct ordering
 */
export function validateColorStopPositions(
  colorStops: [number, string][],
  changedIndex: number
): string | null {
  const positions = colorStops.map(([pos]) =>
    typeof pos === 'string' ? parseFloat(pos) : pos
  );

  const [low, mid, high] = positions;

  if (isNaN(low) || isNaN(mid) || isNaN(high)) {
    return 'All positions must be valid numbers';
  }

  if (
    low < -100 ||
    low > 100 ||
    mid < -100 ||
    mid > 100 ||
    high < -100 ||
    high > 100
  ) {
    return 'All positions must be between -100 and 100';
  }

  if (changedIndex === 0 && low > mid) {
    return 'Low must be ≤ mid';
  }

  if (changedIndex === 1 && (mid < low || mid > high)) {
    return 'Mid must be between low and high';
  }

  if (changedIndex === 2 && high < mid) {
    return 'High must be ≥ mid';
  }

  return null;
}
